import { useEffect, useMemo, useRef, useState } from 'react';
import { Container, makeStyles } from '@material-ui/core';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import StatisticsGraphs, { StatisticLayout } from '../components/Statistics/StatisticsGraphs';
import WorkloadScale from '../components/shared/WorkloadScale';
import TeamSelect from '../components/shared/TeamSelect';
import { IUsersStatistics } from 'utils/wemble-api.generated';
import { useGetCurrentUserQuery, useGetStatisticsMutation, useGetOfficesQuery } from 'utils/wemble-api';
import PageTitle from 'components/PageTitle';
import axios from 'axios';
import excelIcon from '../assets/icons/excel.svg';
import Button from 'components/shared/Button';
import organization from 'utils/organization';
import { cleanedDateString } from 'components/Home/Forecast/utils';

const useStyles = makeStyles(() => ({
  statistics: {
    width: '100%',
    margin: '0 auto 65px',
    fontFamily: 'Roboto',
    paddingBottom: 30,
  },
  statistics__toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  toolbar__filter: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    gap: 8,
  },
  toolbar__datepicker: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    height: 44,
    paddingInline: 10,
    borderRadius: 12,
    cursor: 'default',
    marginRight: 16,
    '&:hover $datepicker__presets': {
      display: 'flex',
    },
  },
  datepicker__input: {
    backgroundColor: 'white',
    padding: 10,
  },
  datepicker__presets: {
    display: 'none',
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    flexDirection: 'column' as const,
    paddingTop: 4,
    zIndex: 10,
  },
  datepicker__presets_inner: {
    backgroundColor: 'white',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    padding: '4px 0',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  datepicker__preset: {
    padding: '8px 14px',
    fontSize: 13,
    color: '#555',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
}));


const Statistics = () => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [layout, setLayout] = useState<StatisticLayout | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const startDateAdjustedRef = useRef(false);
  const initializedRef = useRef(false);

  const { data: user } = useGetCurrentUserQuery();
  const { data: offices } = useGetOfficesQuery();
  const [getStatistics] = useGetStatisticsMutation();
  const org = organization(user?.company);

  // Default team selection based on org config
  useEffect(() => {
    if (user && offices && !initializedRef.current) {
      initializedRef.current = true;
      const groupId = typeof user.group === 'string' ? user.group : user.group?._id;
      const officeId = typeof user.office === 'string' ? user.office : user.office?._id;

      if (org.allGroupsDefault) {
        setSelectedTeams(['__all__']);
      } else if (org.allOfficesDefault && groupId) {
        const groupOfficeIds = offices?.filter(o => {
          const oGroupId = typeof o.group === 'string' ? o.group : o.group?._id;
          return oGroupId === groupId;
        }).map(o => o._id!).filter(Boolean) || [];
        setSelectedTeams(groupOfficeIds.length > 0 ? groupOfficeIds : ['__all__']);
      } else if (officeId) {
        setSelectedTeams([officeId]);
      } else {
        setSelectedTeams(['__all__']);
      }
    }
  }, [user, offices]);

  // Send team IDs directly to the backend
  const filters = useMemo(() => {
    if (!selectedTeams.length) return undefined;
    if (selectedTeams[0] === '__all__') return undefined; // No filter = all
    return { teams: selectedTeams };
  }, [selectedTeams]);

  const downloadXlsx = () => {
    if (!startDate || !user?._id) return;
    axios({
      url: '/api/v2/statistic/export',
      method: 'POST',
      responseType: 'blob',
      params: { startDate: cleanedDateString(startDate), endDate: cleanedDateString(endDate) },
      data: layout === 'user' ? { filters: { users: [user._id] } } : { filters }
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      const start = cleanedDateString(startDate).split('T')[0];
      const end = cleanedDateString(endDate).split('T')[0];
      link.setAttribute('download', `Wemble_export_${start}_${end}.xlsx`);
      document.body.appendChild(link);
      link.click();
    });
  };

  useEffect(() => {
    if (!layout && user) {
      setLayout(user.administratorPriviliges || user.admin ? 'admin' : 'user');
    }
  }, [layout, user]);

  const [statistics, setStatistics] = useState<IUsersStatistics | null>(null);

  // Sets the default start date to 4 months ago. The backend will adjust
  // (clamp) this to the earliest user creation date if needed.
  useEffect(() => {
    if (!startDate && user) {
      const defaultStartDate = new Date(
        moment(new Date())
          .subtract(6, 'months')
          .format('YYYY-MM-DD'),
      );
      setStartDate(defaultStartDate);
    }
  }, [startDate, user]);

  const hasFilters = selectedTeams.length > 0;

  useEffect(() => {
    if (startDateAdjustedRef.current) {
      startDateAdjustedRef.current = false;
      return;
    }

    if (layout === 'admin' && hasFilters && startDate) {
      setStatistics(null);
      setErrorMsg(null);
      getStatistics({
        startDate: cleanedDateString(startDate),
        endDate: cleanedDateString(endDate),
        body: {
          filters: filters || {},
        } as any,
      })
        .unwrap()
        .then((data) => {
          const adjusted = (data as any).adjustedStartDate;
          if (adjusted) {
            startDateAdjustedRef.current = true;
            setStartDate(new Date(adjusted));
          }
          setStatistics(data);
        })
        .catch((err) => {
          setErrorMsg(err?.data?.message);
        });
    } else if (layout === 'user' && user?._id && startDate) {
      setStatistics(null);
      setErrorMsg(null);
      getStatistics({
        startDate: cleanedDateString(startDate),
        endDate: cleanedDateString(endDate),
        body: {
          filters: {
            users: [user._id],
          },
        },
      })
        .unwrap()
        .then((data) => {
          const adjusted = (data as any).adjustedStartDate;
          if (adjusted) {
            startDateAdjustedRef.current = true;
            setStartDate(new Date(adjusted));
          }
          setStatistics(data);
        })
        .catch((err) => {
          setErrorMsg(err?.data?.message);
        });
    }
  }, [startDate, endDate, filters, layout, hasFilters]);

  return (
    <>
      <Container>
        <PageTitle title="Statistics" />
        <div className={classes.statistics}>
          <div className={classes.statistics__toolbar}>
            <div className={classes.toolbar__filter}>
              {layout === 'admin' && (
                <TeamSelect
                  value={selectedTeams}
                  onChange={setSelectedTeams}
                  restrictToUserGroups={!user?.companyAdmin}
                />
              )}
            </div>
            <div className={classes.toolbar__datepicker}>
              <DatePicker
                wrapperClassName={'datePickerCustomStyling'}
                selected={startDate}
                maxDate={new Date()}
                onChange={(date) => setStartDate(date)}
                startDate={startDate}
                endDate={endDate}
                placeholderText={'Start date'}
                calendarStartDay={1}
                formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
                customInput={
                  <button
                    type="button"
                    className={classes.datepicker__input}
                    style={startDate ? { opacity: 1 } : undefined}
                  >
                    {cleanedDateString(startDate).split('T')[0] ?? ''}
                  </button>
                }
              />
              -
              <DatePicker
                wrapperClassName={'datePickerCustomStyling'}
                selected={endDate}
                maxDate={new Date()}
                onChange={(date) => setEndDate(date)}
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={'End date'}
                calendarStartDay={1}
                formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3)}
                customInput={
                  <button
                    type="button"
                    className={classes.datepicker__input}
                    style={endDate ? { opacity: 1 } : undefined}
                  >
                    {cleanedDateString(endDate).split('T')[0] ?? ''}
                  </button>
                }
              />
              <div className={classes.datepicker__presets}>
                <div className={classes.datepicker__presets_inner}>
                  {[
                    { label: 'Last 30 days', days: 30 },
                    { label: 'Last 3 months', days: 90 },
                    { label: 'Last 6 months', days: 182 },
                    { label: 'Last year', days: 365 },
                  ].map(({ label, days }) => {
                    const isActive = startDate && Math.abs(moment(endDate).diff(moment(startDate), 'days') - days) <= 2;
                    return (
                      <button
                        key={days}
                        type="button"
                        className={classes.datepicker__preset}
                        style={isActive ? { backgroundColor: '#f0f0f0', fontWeight: 600 } : undefined}
                        onClick={() => {
                          setEndDate(new Date());
                          setStartDate(moment().subtract(days, 'days').toDate());
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <Button onClick={downloadXlsx} text="&nbsp;&nbsp;&nbsp;Download report&nbsp;" icon={<img alt="Excel" src={excelIcon} style={{ width: 25, height: 25, maxWidth: 'none' }} />} />

          </div>
          {errorMsg ? (
            errorMsg
          ) : (
            <>
              {statistics && startDate && moment(endDate).diff(moment(startDate), 'days') < 3 && (
                <p style={{ color: '#999', fontSize: 14, marginBottom: 0 }}>
                  Limited data available — the selected range only covers {moment(endDate).diff(moment(startDate), 'days') + 1} days.
                </p>
              )}
              <StatisticsGraphs
                statistics={layout === 'admin' && !hasFilters ? null : statistics}
                layout={layout}
                showTeams={Object.keys(statistics?.teamWorkload ?? {}).length > 1}
              />
            </>
          )}
        </div>
      </Container>

      {!errorMsg && (layout === 'admin' ? hasFilters : layout === 'user') && statistics && (
        <WorkloadScale />
      )}
    </>
  );
};

export default Statistics;
